import { useNavigate } from "react-router-dom";

import { type FlexMenu, type FlexProduct } from "~/api/menus/types";
import { useCurrentTillLocalisation } from "~/api/tills/hooks/use-till-current/localisation";
import { priceFormatter } from "~/utils/price-formatter";
import { useCollectAllergyMatcher } from "~/hooks/use-collect-allergy-matcher";
import { type Currency } from "~/api/products/types";

import { OrdersList } from "../__app.collect.orders/basket/orders-list";
import {
  useCollectPreOrderLines,
  useOrdersCreateActions
} from "../__app.collect.orders.create/orders-create-context";
import { type TakeCandidate } from "../__app.collect.orders.create/use-collect-debt-gate";
import { pricePreOrderLine } from "../__app.collect.orders.create/price-pre-order-line";

import { parseVariantChoices } from "./parse-variant-choices";

type MenuItemProps = {
  product: FlexProduct;
  variants: FlexMenu["variants"];
  moduleId: string;
  discounts: FlexMenu["discounts"];
  currency: Currency;
  childId: string;
  takeCandidate: TakeCandidate;
};

export const MenuItem = ({
  product,
  variants,
  moduleId,
  discounts,
  currency,
  childId,
  takeCandidate
}: MenuItemProps) => {
  const navigate = useNavigate();

  const { locale } = useCurrentTillLocalisation();
  const matchAllergies = useCollectAllergyMatcher({ childId, moduleId });
  const preOrderLines = useCollectPreOrderLines();
  const { togglePreOrderLine } = useOrdersCreateActions();

  const productSelected = preOrderLines.find(
    (line) => line.moduleId === moduleId && line.id === product.id
  );

  const { options } = parseVariantChoices(variants, productSelected);

  const { unitPriceInclVat, depositInclVat, variantDeposits } =
    pricePreOrderLine(
      product,
      variants,
      discounts,
      Object.fromEntries(
        options.map((option) => [option.id, option.amount] as const)
      )
    );

  const productDepositInclVat = product.deposit?.feeInclVat ?? 0;

  const formatPrice = (value: number) =>
    priceFormatter({ value, currency, locale });

  const formattedPrice = formatPrice(unitPriceInclVat + depositInclVat);

  const formattedDeposit = productDepositInclVat
    ? formatPrice(productDepositInclVat)
    : undefined;

  const formattedVariantDeposits = Object.fromEntries(
    options.flatMap((option) => {
      const deposit = variantDeposits.byChoiceId[option.id];
      return deposit
        ? [[option.id, formatPrice(deposit.feeInclVat * option.amount)]]
        : [];
    })
  );

  const handleClick = () => {
    if (product.variantIds?.length && !productSelected) {
      navigate(
        `/collect/orders/create/products/${product.id}?childId=${childId}&moduleId=${moduleId}`
      );
      return;
    }

    const candidate = {
      id: product.id,
      moduleId,
      unitPriceInclVat,
      depositInclVat
    };

    if (productSelected) {
      togglePreOrderLine(candidate);
      return;
    }

    takeCandidate({ candidate, productName: product.name });
  };

  return (
    <OrdersList.Item
      key={product.id}
      options={options}
      name={product.name}
      description={product.description ?? undefined}
      isSelected={Boolean(productSelected)}
      matchAllergies={matchAllergies}
      type={product.variantIds?.length ? "variant" : "regular"}
      onClick={handleClick}
      productAllergens={product.allergens}
      price={formattedPrice}
      deposit={formattedDeposit}
      variantDeposits={formattedVariantDeposits}
      variant="menu"
    />
  );
};
